import { useAppDispatch } from "../../../../redux/store";
import { useSnackbar } from "notistack";
import {
  downloadDropboxFile,
  getDropboxTemporaryLink,
  getDropboxPreview,
  getDropboxThumbnail,
} from "../../../../redux/slices/dropboxFoldersSlice";

export const useFileOperations = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const handleFileDownload = async (file: any) => {
    if (!file.path) return;

    try {
      const result = await dispatch(
        downloadDropboxFile({
          filePath: file.path,
        })
      ).unwrap();

      // The temporary link is in the 'link' property of the result
      const downloadUrl = result?.link;
      if (downloadUrl) {
        // Use fetch to download the file as blob to ensure proper handling
        const response = await fetch(downloadUrl);
        const blob = await response.blob();

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        enqueueSnackbar("File downloaded successfully", { variant: "success" });
      }
    } catch (error) {
      console.error("Failed to download file:", error);
      enqueueSnackbar("Failed to download file", { variant: "error" });
    }
  };

  const handleFilePreview = (file: any) => {
    if (file.type === "file" && file.path) {
      // For PDF files, use temporary link action to get the PDF URL
      if (isPdfFile(file.name)) {
        dispatch(getDropboxTemporaryLink({ filePath: file.path }));
      } else if (isVideoFile(file.name)) {
        // For video files, use temporary link to get the video URL
        dispatch(getDropboxTemporaryLink({ filePath: file.path }));
      } else if (
        isPreviewableAsPdf(file.name) ||
        isPreviewableAsHtml(file.name)
      ) {
        // For files that can be previewed via Dropbox API
        dispatch(getDropboxPreview({ filePath: file.path }));
      } else {
        // For other files, fetch larger thumbnail
        dispatch(
          getDropboxThumbnail({ filePath: file.path, size: "w1024h768" })
        );
      }
    }
  };

  return {
    handleFileDownload,
    handleFilePreview,
  };
};

// File type detection utilities
export const isPdfFile = (fileName: any) => {
  if (!fileName) return false;
  const ext = fileName.split(".").pop()?.toLowerCase();
  return ext === "pdf";
};

export const isVideoFile = (fileName: any) => {
  if (!fileName) return false;
  const ext = fileName.split(".").pop()?.toLowerCase();
  const videoExtensions = [
    "mp4",
    "avi",
    "mov",
    "mkv",
    "webm",
    "wmv",
    "flv",
    "m4v",
    "3gp",
    "ogv",
  ];
  return videoExtensions.includes(ext);
};

export const isImageFile = (fileName: any) => {
  if (!fileName) return false;
  const ext = fileName.split(".").pop()?.toLowerCase();
  const imageExtensions = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "svg",
    "webp",
    "tiff",
    "tif",
    "ico",
    "heic",
    "heif",
  ];
  return imageExtensions.includes(ext);
};

export const isPreviewableAsPdf = (fileName: any) => {
  if (!fileName) return false;
  const ext = fileName.split(".").pop()?.toLowerCase();
  const pdfPreviewExtensions = [
    "ai",
    "doc",
    "docm",
    "docx",
    "eps",
    "gdoc",
    "gslides",
    "odp",
    "odt",
    "pps",
    "ppsm",
    "ppsx",
    "ppt",
    "pptm",
    "pptx",
    "rtf",
  ];
  return pdfPreviewExtensions.includes(ext);
};

export const isPreviewableAsHtml = (fileName: any) => {
  if (!fileName) return false;
  const ext = fileName.split(".").pop()?.toLowerCase();
  const htmlPreviewExtensions = ["csv", "ods", "xls", "xlsm", "gsheet", "xlsx"];
  return htmlPreviewExtensions.includes(ext);
};
